import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  TextField,
  FormHelperText,
  Alert,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormikProps, useFormik } from 'formik';
import useI18nForm from '../hooks/useI18nForm';
import * as Yup from 'yup';
import { formatDateToISO } from '../FieldFormat';
import { Gender, InvoicePreference } from '../../../services/profile/constants';
import FamiliplusBonusPoints from './FamiliplusBonusPoints';

const InvoicePreferenceField = (props: { formik: FormikProps<ShoppingPreferencesFormValues> }): JSX.Element => {
  const { t } = useTranslation('component');
  return (
    <FormControl>
      <FormLabel htmlFor="InvoicePreference_RadioGroup">
        {t('Form.InvoicePreference') + ' ' + t('Form.Optional')}
      </FormLabel>
      <RadioGroup
        id="InvoicePreference_RadioGroup"
        name="InvoicePreference"
        defaultValue={props.formik.initialValues.InvoicePreference}
        onChange={props.formik.handleChange}>
        <FormControlLabel value={InvoicePreference.Email} control={<Radio />} label={t('InvoicePreference.Email')} />
        <FormControlLabel value={InvoicePreference.Both} control={<Radio />} label={t('InvoicePreference.Both')} />
        <FormControlLabel value={InvoicePreference.Paper} control={<Radio />} label={t('InvoicePreference.Paper')} />
      </RadioGroup>
    </FormControl>
  );
};
const GenderPreference = (props: { formik: FormikProps<ShoppingPreferencesFormValues> }): JSX.Element => {
  const { t } = useTranslation('component');
  return (
    <FormControl>
      <FormLabel htmlFor="GenderPreference_RadioGroup">
        {t('Form.GenderPreference') + ' ' + t('Form.Optional')}
      </FormLabel>
      <RadioGroup
        id="GenderPreference_RadioGroup"
        name="GenderPreference"
        defaultValue={props.formik.initialValues.GenderPreference}
        onChange={props.formik.handleChange}>
        <FormControlLabel value={Gender.Male} control={<Radio />} label={t('GenderPreference.Male')} />
        <FormControlLabel value={Gender.Female} control={<Radio />} label={t('GenderPreference.Female')} />
        <FormControlLabel value={Gender.Other} control={<Radio />} label={t('GenderPreference.Other')} />
      </RadioGroup>
    </FormControl>
  );
};

const Birthday = (props: { formik: FormikProps<ShoppingPreferencesFormValues>; minDate: Date; maxDate: Date }) => {
  const { t } = useTranslation('component');

  return (
    <FormControl error={props.formik.touched['BirthdayDate'] && Boolean(props.formik.errors['BirthdayDate'])}>
      <FormLabel htmlFor="BirthdayTextInput">{t('Form.Birthday') + ' ' + t('Form.Optional')}</FormLabel>
      <TextField
        onChange={props.formik.handleChange}
        onBlur={(e) => {
          if (isNaN(Date.parse(e.target.value))) {
            props.formik.values.BirthdayDate = null;
            e.target.value = '';
          }
        }}
        sx={{ width: '175px' }}
        id="BirthdayTextInput"
        name="BirthdayDate"
        type="date"
        defaultValue={props.formik.values.BirthdayDate ? props.formik.values.BirthdayDate : null}
        error={props.formik.touched['BirthdayDate'] && Boolean(props.formik.errors['BirthdayDate'])}
        InputProps={{ inputProps: { min: formatDateToISO(props.minDate), max: formatDateToISO(props.maxDate) } }}
      />
      {props.formik.touched['BirthdayDate'] && Boolean(props.formik.errors['BirthdayDate']) && (
        <FormHelperText id="BirthdayErrorText">{props.formik.errors['BirthdayDate']}</FormHelperText>
      )}
    </FormControl>
  );
};

export interface ShoppingPreferencesFormValues {
  InvoicePreference: InvoicePreference;
  GenderPreference: Gender;
  BirthdayDate: string | null;
}

interface ShoppingPreferenceFormProps {
  shoppingPreferences: ShoppingPreferencesFormValues;
  isFamiliplusMember: boolean;
  saveHandler: (values: ShoppingPreferencesFormValues) => void;
  saveErrorMessage?: string | null;
  footer: JSX.Element;
  showPtsBox: boolean;
}

const ShoppingPreferenceForm = ({
  shoppingPreferences,
  isFamiliplusMember,
  saveHandler,
  saveErrorMessage,
  footer,
  showPtsBox,
}: ShoppingPreferenceFormProps): JSX.Element => {
  const { t } = useTranslation('component');

  const minBirthdayDate = new Date('1899-12-31');
  const maxBirthdayDate = new Date();
  maxBirthdayDate.setFullYear(maxBirthdayDate.getFullYear() - 14);

  const validationSchema = Yup.object().shape({
    BirthdayDate: Yup.date()
      .nullable()
      .min(formatDateToISO(minBirthdayDate), t('FormValidations.InvalidBirthday').toString())
      .max(formatDateToISO(maxBirthdayDate), t('FormValidations.InvalidBirthday').toString())
      .transform((v, o) => {
        return o && isNaN(Date.parse(o)) ? null : new Date(o);
      }),
  });

  const formik = useFormik({
    initialValues: shoppingPreferences,
    validationSchema: validationSchema,
    onSubmit: (values: ShoppingPreferencesFormValues) => {
      saveHandler(values);
    },
  });

  useI18nForm({ errors: formik.errors, touched: formik.touched, setFieldTouched: formik.setFieldTouched });

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      {saveErrorMessage && (
        <Alert severity="error" sx={{ mb: '16px' }}>
          {saveErrorMessage}
        </Alert>
      )}
      <Grid container spacing={3}>
        {isFamiliplusMember && (
          <Grid item xs={12}>
            <InvoicePreferenceField formik={formik} />
          </Grid>
        )}
        <Grid item xs={12}>
          <GenderPreference formik={formik} />
        </Grid>
        <Grid item xs={12}>
          <Birthday formik={formik} minDate={minBirthdayDate} maxDate={maxBirthdayDate} />
        </Grid>
        {showPtsBox && (
          <Grid item xs={12}>
            <FamiliplusBonusPoints></FamiliplusBonusPoints>
          </Grid>
        )}
      </Grid>
      <Divider />
      {footer}
    </form>
  );
};

export default ShoppingPreferenceForm;
